import React, { useMemo, useState } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';
import { ChevronDownIcon } from 'lucide-react';
import { categories } from '../data/collections';
import { mainNav } from '../data/navigation';
import { fromPrice, productsForCollection, resolveCollection } from '../utils/products';
import { ProductCard } from '../components/shop/ProductCard';
import { NotFound } from './NotFound';
import type { CategoryId, Collection as CollectionType } from '../types/product';
type Sort = 'populair' | 'prijs-op' | 'prijs-af' | 'beoordeling';
const sortLabels: Record<Sort, string> = {
  populair: 'Populairst',
  'prijs-op': 'Prijs: laag naar hoog',
  'prijs-af': 'Prijs: hoog naar laag',
  beoordeling: 'Best beoordeeld'
};
export function Collection() {
  const {
    slug = 'alles'
  } = useParams();
  const collection = resolveCollection(slug);
  if (!collection) return <NotFound data-id="element-460" data-mp="r96vj1vfr0bj" />;
  return <CollectionView key={slug} collection={collection} data-id="element-461" data-mp="r9ozyls94kuj" />;
}
function CollectionView({
  collection
}: {
  collection: CollectionType;
}) {
  const [category, setCategory] = useState<CategoryId | 'alle'>('alle');
  const [sort, setSort] = useState<Sort>('populair');
  const all = productsForCollection(collection);
  const present = categories.filter(c => all.some(p => p.category === c.id));
  const visible = useMemo(() => {
    const list = category === 'alle' ? all : all.filter(p => p.category === category);
    const sorted = [...list];
    if (sort === 'prijs-op') sorted.sort((a, b) => fromPrice(a) - fromPrice(b));
    if (sort === 'prijs-af') sorted.sort((a, b) => fromPrice(b) - fromPrice(a));
    if (sort === 'beoordeling') sorted.sort((a, b) => b.rating - a.rating || b.reviews - a.reviews);
    return sorted;
  }, [all, category, sort]);
  return <div className="mx-auto max-w-7xl px-5 pb-24 pt-8 lg:px-8 lg:pt-10" data-id="element-462" data-mp="10jrd6l7d7q21">
      <nav aria-label="Kruimelpad" className="text-sm text-muted" data-id="element-463" data-mp="27z4jsbzahax6">
        <Link to="/" className="hover:text-ink" data-id="element-464" data-mp="2c0hnafewt20x">Home</Link>
        <span className="mx-2" data-id="element-465" data-mp="2c33vmh9mjlk7">/</span>
        <span className="text-ink" data-id="element-466" data-mp="cvpyg6x05r69">{collection.title}</span>
      </nav>

      <div className="mt-6 grid gap-8 lg:grid-cols-12 lg:items-end" data-id="element-467" data-mp="7xogjpbbbxwd">
        <div className="lg:col-span-7" data-id="element-468" data-mp="7ziqy9oyk7m7">
          <h1 className="text-4xl font-semibold tracking-display text-ink sm:text-6xl" data-id="element-469" data-mp="lxphf4rkv5bu">{collection.title}</h1>
          <p className="mt-3 max-w-xl text-lg text-muted" data-id="element-470" data-mp="gxurp4jjglsd">{collection.intro}</p>
        </div>
        {collection.image && collection.kind === 'audience' && <img src={collection.image} alt="" className="hidden aspect-[16/9] w-full rounded-2xl object-cover object-[50%_25%] lg:col-span-5 lg:block" data-id="element-471" data-mp="3pbjru1ysb5s9" />}
      </div>

      <div className="no-scrollbar -mx-5 mt-8 flex gap-2 overflow-x-auto px-5 lg:mx-0 lg:px-0" data-id="element-472" data-mp="2zwg26wlr3gk">
        {mainNav.map(n => <NavLink key={n.to} to={n.to} className={({
        isActive
      }) => `whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150 ${isActive ? 'bg-ink text-canvas' : 'bg-surface text-ink hover:bg-line'}`} data-id="element-473" data-mp="3ub68ymhqh66c">
            {n.label}
          </NavLink>)}
      </div>

      <div className="mt-8 flex flex-col gap-4 border-y border-line py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-474" data-mp="185l1gmern7ls">
        {collection.kind !== 'category' && present.length > 1 ? <div className="no-scrollbar -mx-5 flex gap-1 overflow-x-auto px-5 sm:mx-0 sm:px-0" role="group" aria-label="Filter op categorie" data-id="element-475" data-mp="20ua974abub6i">
            {[{
          id: 'alle' as const,
          title: 'Alles'
        }, ...present].map(c => <button key={c.id} onClick={() => setCategory(c.id)} aria-pressed={category === c.id} className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors duration-150 ${category === c.id ? 'bg-surface font-medium text-ink' : 'text-muted hover:text-ink'}`} data-id="element-476" data-mp="1qwm35i79th91">
                {c.title}
              </button>)}
          </div> : <p className="text-sm text-muted" data-id="element-477" data-mp="uz7062t331ak">{visible.length} producten</p>}
        <div className="flex items-center justify-between gap-4 sm:justify-end" data-id="element-478" data-mp="qxd5qc9hetiu">
          {collection.kind !== 'category' && present.length > 1 && <p className="text-sm text-muted" data-id="element-479" data-mp="lzbo7vrpf29h">{visible.length} producten</p>}
          <label className="relative" data-id="element-480" data-mp="1io9m3mur3gav">
            <span className="sr-only" data-id="element-481" data-mp="1inzr1kuumiyb">Sorteren</span>
            <select value={sort} onChange={e => setSort(e.target.value as Sort)} className="h-10 appearance-none rounded-full border border-line bg-white pl-4 pr-10 text-sm font-medium text-ink focus:border-ink focus:outline-none" data-id="element-482" data-mp="1en7ikttswi33">
              {(Object.keys(sortLabels) as Sort[]).map(s => <option key={s} value={s} data-id="element-483" data-mp="3ufdi03m2ll29">{sortLabels[s]}</option>)}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" data-id="element-484" data-mp="3pdo5skn431cl" />
          </label>
        </div>
      </div>

      {visible.length === 0 ? <div className="py-24 text-center" data-id="element-485" data-mp="3kaexbp7v4hex">
          <p className="text-lg font-semibold" data-id="element-486" data-mp="3p8ouu6h27u3p">Geen producten in deze selectie</p>
          <button onClick={() => setCategory('alle')} className="mt-4 text-sm font-medium underline underline-offset-4" data-id="element-487" data-mp="3tzyi50f4ylcf">
            Toon alles
          </button>
        </div> : <ul className="mt-10 grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-3 lg:grid-cols-4 lg:gap-x-6" data-id="element-488" data-mp="1yiea3erp5ae">
          {visible.map(p => <li key={p.slug} data-id="element-489" data-mp="bu2akqx8u5zo">
              <ProductCard product={p} data-id="element-490" data-mp="buvbvcdfo3af" />
            </li>)}
        </ul>}
    </div>;
}